/**
 * InfiniteScrollSentinel (InfiniteScrollSentinel.jsx)
 * 
 * \src\components\IncomingOrders\IncomingOrdersTable\InfiniteScrollSentinel.jsx
 * 
 * A sentinel component that triggers a callback when it becomes visible in the viewport.
 * Typically used for infinite scrolling lists to load more items when the user scrolls near the bottom.
 */
import PropTypes from "prop-types";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

/**
 * Utility function to merge multiple refs into a single ref callback.
 * 
 * @param  {...any} refs - List of refs (object refs or callback refs)
 * @returns {function} A function that assigns the instance to all provided refs
 */
function mergeRefs(...refs) {
  return (instance) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(instance);
      } else {
        ref.current = instance;
      }
    });
  };
}

/**
 * InfiniteScrollSentinel component.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isActive - Whether the sentinel is currently active
 * @param {boolean} props.isLoading - Whether more items are currently being loaded
 * @param {function} props.onVisible - Callback invoked when the sentinel becomes visible
 * @param {React.ReactNode|string} [props.scrollText="Scroll down for more…"] - Text or component shown when active and not loading
 * @param {React.ReactNode|string} [props.loadingText="Loading more…"] - Text or component shown when loading
 * @param {object} [props.sx={}] - Optional Material UI sx prop for custom styling
 * @param {object} [props.observerOptions={ threshold: 1.0 }] - Options for IntersectionObserver
 * @param {React.Ref} ref - Ref forwarded to the root Box element
 */
const InfiniteScrollSentinel = forwardRef(
  (
    {
      isActive,
      isLoading,
      onVisible,
      scrollText = "Scroll down for more…",
      loadingText = "Loading more…",
      sx = {},
      observerOptions = { threshold: 1.0 },
    },
    ref
  ) => {
    const sentinelRef = useRef(null);
    const [hasTriggered, setHasTriggered] = useState(false);

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el || !isActive || isLoading) {
        setHasTriggered(false);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasTriggered) {
            onVisible?.();
            setHasTriggered(true);
          }
        },
        observerOptions
      );

      observer.observe(el);

      return () => observer.disconnect();
    }, [isActive, isLoading, onVisible, observerOptions, hasTriggered]);

    const renderContent = () => (isLoading ? loadingText : scrollText);

    return (
      <Box
        ref={mergeRefs(ref, sentinelRef)}
        component="div"
        sx={{
          py: 2,
          textAlign: "center",
          minHeight: "1px",
          visibility: isActive ? "visible" : "hidden",
          ...sx,
        }}
      >
        {isActive && renderContent()}
      </Box>
    );
  }
);

InfiniteScrollSentinel.propTypes = {
  isActive: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadingText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  scrollText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onVisible: PropTypes.func.isRequired,
  sx: PropTypes.object,
  observerOptions: PropTypes.shape({
    root: PropTypes.any,
    rootMargin: PropTypes.string,
    threshold: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  }),
};

InfiniteScrollSentinel.defaultProps = {
  loadingText: "Loading more…",
  scrollText: "Scroll down for more…",
  sx: {},
  observerOptions: { threshold: 1.0 },
};

InfiniteScrollSentinel.displayName = "InfiniteScrollSentinel";

export default InfiniteScrollSentinel;
